import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn';
import styles from './Tooltip.module.css';

const DEFAULT_DELAY_MS = 300;

export type TooltipProps = {
  /** Только текст (docs/06 §4). */
  readonly content: string;
  readonly delay?: number;
  readonly children: ReactNode;
  readonly side?: 'top' | 'bottom';
};

/**
 * Tooltip на CSS + таймере, без внешних зависимостей.
 * Показывается по hover и по фокусу, доступен с клавиатуры.
 */
export function Tooltip({ content, delay = DEFAULT_DELAY_MS, children, side = 'top' }: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const show = useCallback(() => {
    timerRef.current = window.setTimeout(() => {
      const node = anchorRef.current;
      if (!node) {
        return;
      }
      const rect = node.getBoundingClientRect();
      setCoords({ x: rect.left + rect.width / 2, y: side === 'top' ? rect.top : rect.bottom });
      setIsOpen(true);
    }, delay);
  }, [delay, side]);

  const hide = useCallback(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    setIsOpen(false);
  }, []);

  useEffect(() => hide, [hide]);

  return (
    <span
      ref={anchorRef}
      className={styles['anchor']}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {isOpen && coords
        ? createPortal(
            <span
              role="tooltip"
              className={cn(styles['tooltip'], side === 'top' ? styles['top'] : styles['bottom'])}
              style={{ left: coords.x, top: coords.y }}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
